import { Dispatch, FC, SetStateAction, useEffect, useLayoutEffect, useState } from "react";
import { PdfControls } from "./generic/PdfControls";
import { calculatePageRanges } from "./generic/PdfHelpers";
import { PdfLoader } from "./generic/PdfLoader";
import { DefaultPdfProps } from "./generic/PdfTypes";
import { PdfViewer } from "./generic/PdfViewer";
import * as Styled from "./PdfInStepper.styled";

/**
 * @param filePath - Specifies path to file
 * @param page - If provided, pdf will display this given page (indexed from 1)
 * @param lastPage - If provided, pdf will display pages from page to lastPage
 * @param pageCount - If provided, pdf will display pageCount pages from page
 * If no @param page is provided, the entire PDF will be shown
 */
type PdfInStepperProps = {
    setNextButtonDisabled: Dispatch<SetStateAction<boolean>>;
    onPageCountChange?: (pageCount: number) => void;
} & DefaultPdfProps;

export const PdfInStepper: FC<PdfInStepperProps> = (props) => {
    const {
        fileContent,
        page,
        lastPage,
        pageCount,
        scale: propsScale,
        setNextButtonDisabled,
        onPageCountChange,
    } = props;

    const [file, setFile] = useState<Uint8Array | null>(null);
    const [totalPages, setTotalPages] = useState<number | null>(null);
    const [startPage, setStartPage] = useState<number>(1);
    const [endPage, setEndPage] = useState<number>(1);
    const [scale, setScale] = useState<number>(propsScale ?? 1);

    // Load PDF document when fileContent is provided
    useLayoutEffect(() => {
        (async () => {
            if (!fileContent || fileContent.byteLength === 0) return;
            setNextButtonDisabled(true);
            const res = await PdfLoader({ fileContent });
            setFile(res.file);
            setTotalPages(res.totalPages);
            const [start, end] = calculatePageRanges(res.totalPages, page, lastPage, pageCount);

            setStartPage(start);
            setEndPage(end);
        })();
    }, [fileContent, lastPage, page, pageCount, setNextButtonDisabled]);

    // inform parent about total page count when we load the document
    useEffect(() => {
        if (totalPages) onPageCountChange?.(totalPages);
    }, [onPageCountChange, totalPages]);

    return (
        <Styled.PdfWrapper>
            <Styled.DocumentWrapper>
                <PdfViewer
                    file={file}
                    onLoaded={() => setNextButtonDisabled(false)}
                    startPage={startPage}
                    scale={scale}
                    endPage={endPage}
                />
            </Styled.DocumentWrapper>

            <PdfControls scaleState={[scale, setScale]} />
        </Styled.PdfWrapper>
    );
};
